import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Zap, Bell, Rocket, Lock, ArrowRight } from "lucide-react";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: [0.2, 0.8, 0.2, 1],
    },
  }),
};

const pulseGlow = {
  initial: { boxShadow: "0 0 0 0 hsla(350,58%,42%,0)" },
  animate: {
    boxShadow: [
      "0 0 0 0 hsla(350,58%,42%,0.3)",
      "0 0 30px 8px hsla(350,58%,42%,0.08)",
      "0 0 0 0 hsla(350,58%,42%,0)",
    ],
    transition: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
  },
};

export default function SuccessPage() {
  const navigate = useNavigate();

  return (
    <div className="typeform-bg">
      <div className="typeform-sheen" />

      {/* Powered by footer */}
      <div className="fixed bottom-3 left-0 right-0 z-40 text-center pointer-events-none">
        <span className="text-[0.55rem] font-mono uppercase tracking-[0.15em] text-[hsl(240,3%,52%)]">
          Powered by UNDERADS
        </span>
      </div>

      {/* Animated background elements */}
      <div className="intro-grid" style={{ opacity: 0.15 }} />
      <div className="intro-glow-orb" />

      <div className="typeform-slide-container">
        <div className="typeform-slide" style={{ maxWidth: "640px" }}>

          {/* Top badge */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeIn}
            className="text-center mb-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[hsl(350,58%,42%,0.2)] bg-[hsl(350,58%,42%,0.05)]">
              <span className="status-dot" />
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[hsl(350,58%,48%)]">
                Application Locked In
              </span>
            </div>
          </motion.div>

          {/* Main icon */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeIn}
            className="text-center mb-6"
          >
            <motion.div
              variants={pulseGlow}
              initial="initial"
              animate="animate"
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[hsl(350,58%,42%,0.1)] border border-[hsl(350,58%,42%,0.15)]"
            >
              <Rocket className="h-9 w-9 text-[hsl(350,58%,48%)]" />
            </motion.div>
          </motion.div>

          {/* Big headline */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeIn}
            className="text-center mb-4"
          >
            <h1
              className="font-display text-3xl sm:text-4xl md:text-5xl font-normal tracking-[-0.03em] text-white leading-tight"
              data-testid="success-title"
            >
              Something Big
              <br />
              <span className="text-[hsl(350,58%,48%)]">Is Coming.</span>
            </h1>
          </motion.div>

          {/* Sub headline */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={3}
            variants={fadeIn}
            className="text-center mb-10"
          >
            <p className="text-sm sm:text-base text-[hsl(240,3%,50%)] leading-relaxed max-w-md mx-auto">
              A major collaboration is in the works.
              Bigger than anything we've done before.
              And you just secured your spot in line.
            </p>
          </motion.div>

          {/* Separator */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.0, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            className="red-line w-32 mx-auto mb-10"
          />

          {/* Info cards */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={4}
            variants={fadeIn}
            className="space-y-4 mb-10"
            data-testid="success-confirmation-card"
          >
            <div className="flex items-start gap-4 p-4 rounded-xl border border-[hsl(240,4%,16%)] bg-[hsl(240,5%,8%)]">
              <div className="w-10 h-10 rounded-lg bg-[hsl(350,58%,42%,0.08)] flex items-center justify-center flex-shrink-0">
                <Lock className="h-5 w-5 text-[hsl(350,58%,48%)]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[hsl(30,8%,90%)]">Your application is locked in</p>
                <p className="text-xs text-[hsl(240,3%,45%)] mt-0.5">
                  We're reviewing every single one personally. No bots. No shortcuts.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl border border-[hsl(240,4%,16%)] bg-[hsl(240,5%,8%)]">
              <div className="w-10 h-10 rounded-lg bg-[hsl(350,58%,42%,0.08)] flex items-center justify-center flex-shrink-0">
                <Zap className="h-5 w-5 text-[hsl(350,58%,48%)]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[hsl(30,8%,90%)]">Big collaboration dropping soon</p>
                <p className="text-xs text-[hsl(240,3%,45%)] mt-0.5">
                  We can't say more yet. But when it drops, you'll understand why we waited.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl border border-[hsl(350,58%,42%,0.1)] bg-[hsl(350,58%,42%,0.03)]">
              <div className="w-10 h-10 rounded-lg bg-[hsl(350,58%,42%,0.1)] flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-[hsl(350,58%,48%)]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">We'll alert you when it's ready</p>
                <p className="text-xs text-[hsl(240,3%,50%)] mt-0.5">
                  Stay close to your Telegram and email. When the signal comes, move fast.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Hype closing line */}
          <motion.p
            initial="hidden"
            animate="visible"
            custom={5}
            variants={fadeIn}
            className="text-center text-sm italic text-[hsl(30,10%,80%)] mb-8"
          >
            "The ones who stayed will eat first."
          </motion.p>

          {/* Bottom data stream */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.8 }}
            className="overflow-hidden mb-6"
          >
            <div className="data-stream">
              SOMETHING BIG /// COLLABORATION INCOMING /// STAY READY /// THE LAST DANCE CAPITAL /// THE ONES WHO STAYED ///
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            custom={6}
            variants={fadeIn}
            className="text-center"
          >
            <button
              onClick={() => navigate("/")}
              className="typeform-ok-btn"
              data-testid="success-primary-cta"
            >
              Back to Home <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
